import React, { Component } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Form,
  FormGroup,
  Input,
  Button,
  Alert,
  Badge
} from 'reactstrap';

const WorkCategoryUnit = {
  Unknown: 0,
  Money: 1,
  Pieces: 2,
  Time: 3,
  Percent: 4
};

const getUnitMeta = (unit) => {
  switch (unit) {
    case WorkCategoryUnit.Money:
      return { icon: '₽', inputLabel: '₽' };
    case WorkCategoryUnit.Pieces:
      return { icon: 'шт', inputLabel: 'шт' };
    case WorkCategoryUnit.Percent:
      return { icon: '%', inputLabel: '₽' };
    case WorkCategoryUnit.Time:
    default:
      return { icon: '⏱️', inputLabel: 'время' };
  }
};

export class EmployeeWorkForm extends Component {
  constructor(props) {
    super(props);
    
    // Получаем токен из URL
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token') || window.location.pathname.split('/').pop();
    
    this.state = {
      token: token,
      employee: null,
      workCategories: [],
      workRecords: [],
      notes: '',
      loading: true,
      error: null,
      success: false,
      submitting: false
    };
  }

  sanitizeNonNegativeInt = (rawValue, maxValue = null) => {
    if (rawValue === '' || rawValue === null || rawValue === undefined) return '';
    const parsed = parseInt(String(rawValue), 10);
    if (Number.isNaN(parsed)) return '';
    const clamped = Math.max(0, parsed);
    if (maxValue !== null && clamped > maxValue) return String(maxValue);
    return String(clamped);
  };

  async componentDidMount() {
    await this.loadEmployeeData();
  }

  loadEmployeeData = async () => {
    try {
      const { token } = this.state;
      
      if (!token) {
        this.setState({ error: 'Токен не найден в URL', loading: false });
        return;
      }

      // Загружаем данные сотрудника
      const employeeResponse = await fetch(`/api/public/employee/${token}`);
      if (!employeeResponse.ok) {
        throw new Error('Сотрудник не найден или неактивен');
      }
      const employee = await employeeResponse.json();

      // Загружаем публичные категории работ
      const categoriesResponse = await fetch(`/api/public/work-categories/${token}`);
      if (!categoriesResponse.ok) {
        throw new Error('Не удалось загрузить виды работ');
      }
      const workCategories = await categoriesResponse.json();

      this.setState({
        employee,
        workCategories,
        loading: false
      });
    } catch (error) {
      this.setState({
        error: error.message || 'Ошибка загрузки данных',
        loading: false
      });
    }
  };

  addWorkRecord = () => {
    const newRecord = {
      workCategoryId: '',
      hours: '',
      minutes: '',
      comment: '',
      quantity: ''
    };

    this.setState({
      workRecords: [newRecord, ...this.state.workRecords]
    });
  };

  updateWorkRecord = (index, field, value) => {
    const workRecords = [...this.state.workRecords];
    workRecords[index][field] = value;
    this.setState({ workRecords });
  };

  removeWorkRecord = (index) => {
    const workRecords = this.state.workRecords.filter((_, i) => i !== index);
    this.setState({ workRecords });
  };

  validateForm = () => {
    const { workRecords } = this.state;

    if (workRecords.length === 0) {
      this.setState({ error: 'Добавьте хотя бы одну запись о работе' });
      return false;
    }

    for (let i = 0; i < workRecords.length; i++) {
      const record = workRecords[i];
      if (!record.workCategoryId) {
        this.setState({ error: `Выберите вид работ для записи ${i + 1}` });
        return false;
      }
      // Get work category to check unit
      const workCategory = this.state.workCategories.find(cat => cat.id === record.workCategoryId);
      if (!workCategory) {
        this.setState({ error: `Неверный вид работ для записи ${i + 1}` });
        return false;
      }

      const unit = workCategory.unit ?? WorkCategoryUnit.Time;
      if (unit === WorkCategoryUnit.Money) {
        const amount = parseInt(record.quantity) || 0;
        if (amount <= 0) {
          this.setState({ error: `Укажите сумму для записи ${i + 1}` });
          return false;
        }
      } else if (unit === WorkCategoryUnit.Percent) {
        const amount = parseInt(record.quantity) || 0;
        if (amount <= 0) {
          this.setState({ error: `Укажите сумму (₽) для записи ${i + 1}` });
          return false;
        }
      } else if (unit === WorkCategoryUnit.Pieces) {
        const qty = parseInt(record.quantity) || 0;
        if (qty <= 0) {
          this.setState({ error: `Укажите количество (шт) для записи ${i + 1}` });
          return false;
        }
      } else {
        const hours = parseInt(record.hours) || 0;
        const minutes = parseInt(record.minutes) || 0;
        if (minutes > 60) {
          this.setState({ error: `Минуты не должны превышать 60 для записи ${i + 1}` });
          return false;
        }
        if (hours === 0 && minutes === 0) {
          this.setState({ error: `Укажите время для записи ${i + 1}` });
          return false;
        }
      }
    }

    return true;
  };

  submitWorkAct = async () => {
    if (!this.validateForm()) return;

    this.setState({ submitting: true, error: null });

    try {
      const { token, workRecords, notes } = this.state;

      const requestData = {
        workRecords: workRecords.map(record => {
          const workCategory = this.state.workCategories.find(cat => cat.id === record.workCategoryId);
          const unit = workCategory?.unit ?? WorkCategoryUnit.Time;
          return {
            workCategoryId: record.workCategoryId,
            hours: unit === WorkCategoryUnit.Time ? (parseInt(record.hours) || 0) : 0,
            minutes: unit === WorkCategoryUnit.Time ? (parseInt(record.minutes) || 0) : 0,
            comment: record.comment,
            quantity: unit === WorkCategoryUnit.Time ? null : (parseInt(record.quantity) || 0)
          };
        }),
        notes
      };

      const response = await fetch(`/api/public/work-act/${token}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });

      if (response.ok) {
        this.setState({
          success: true,
          workRecords: [],
          notes: ''
        });
      } else {
        const errorData = await response.json();
        this.setState({ error: errorData.message || 'Не удалось отправить акт' });
      }
    } catch (error) {
      this.setState({ error: 'Ошибка сети' });
    } finally {
      this.setState({ submitting: false });
    }
  };

  formatTime = (hours, minutes) => {
    if (hours === 0 && minutes === 0) return '0';
    if (minutes === 0) return `${hours} ч`;
    if (hours === 0) return `${minutes} мин`;
    return `${hours} ч ${minutes} мин`;
  };

  getTotalTime = () => {
    const { workRecords, workCategories } = this.state;
    const totalMinutes = workRecords.reduce((sum, record) => {
      const workCategory = workCategories.find(cat => cat.id === record.workCategoryId);
      const unit = workCategory?.unit ?? WorkCategoryUnit.Time;
      if (unit !== WorkCategoryUnit.Time) return sum;

      const hours = parseInt(record.hours) || 0;
      const minutes = parseInt(record.minutes) || 0;
      return sum + (hours * 60) + minutes;
    }, 0);

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return this.formatTime(hours, minutes);
  };

  render() {
    const {
      employee,
      workCategories,
      workRecords,
      notes,
      loading,
      error,
      success,
      submitting
    } = this.state;

    if (loading) {
      return (
        <Container className="mt-3">
          <div className="text-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Загрузка...</span>
            </div>
            <p className="mt-2">Загрузка данных...</p>
          </div>
        </Container>
      );
    }

    if (error && !employee) {
      return (
        <Container className="mt-3">
          <Alert color="danger">
            <h4>Ошибка доступа</h4>
            <p>{error}</p>
            <p>Проверьте правильность ссылки или обратитесь к администратору.</p>
          </Alert>
        </Container>
      );
    }

    return (
      <Container className="mt-3" style={{ maxWidth: '600px' }}>
        {/* Заголовок */}
        <Card className="mb-3">
          <CardHeader className="bg-primary text-white">
            <h4 className="mb-0">📋 Отчет о выполненных работах</h4>
          </CardHeader>
          <CardBody>
            <p className="mb-1"><strong>Сотрудник:</strong> {employee?.fullName}</p>
            <p className="mb-0"><strong>Телефон:</strong> {employee?.phone || 'Не указан'}</p>
          </CardBody>
        </Card>

        {/* Сообщения */}
        {error && <Alert color="danger">{error}</Alert>}
        {success && (
          <Alert color="success">
            <h5>✅ Акт отправлен!</h5>
            <p>Ваш отчет о выполненных работах успешно отправлен на проверку менеджеру.</p>
          </Alert>
        )}

        {/* Записи о работах */}
        <Card className="mb-3">
          <CardHeader className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">🔧 Выполненные работы</h5>
            <Button color="success" size="sm" onClick={this.addWorkRecord}>
              + Добавить
            </Button>
          </CardHeader>
          <CardBody className="p-2">
            {workRecords.length === 0 ? (
              <div className="text-center p-3 text-muted">
                Нажмите "Добавить" чтобы добавить запись о работе
              </div>
            ) : (
              workRecords.map((record, index) => {
                const workCategory = workCategories.find(cat => cat.id === record.workCategoryId);
                const unit = workCategory?.unit ?? WorkCategoryUnit.Time;
                const unitMeta = getUnitMeta(unit);

                return (
                  <Card key={index} className="mb-2 border">
                    <CardBody className="p-2">
                      <FormGroup className="mb-2">
                        <div className="d-flex gap-2 align-items-center">
                          <Input
                            type="select"
                            value={record.workCategoryId}
                            onChange={(e) => this.updateWorkRecord(index, 'workCategoryId', e.target.value)}
                          >
                            <option value="">Выберите вид работ</option>
                            {workCategories.map(category => (
                              <option key={category.id} value={category.id}>
                                {category.name}
                              </option>
                            ))}
                          </Input>
                          <Button
                            color="danger"
                            outline
                            onClick={() => this.removeWorkRecord(index)}
                            aria-label="Удалить запись"
                          >
                            ✕
                          </Button>
                        </div>
                      </FormGroup>

                      <FormGroup className="mb-2">
                        {unit === WorkCategoryUnit.Time ? (
                          <div className="d-flex gap-2 align-items-center">
                            <Input
                              type="number"
                              placeholder="часы"
                              min="0"
                              step="1"
                              value={record.hours}
                              onChange={(e) => this.updateWorkRecord(index, 'hours', this.sanitizeNonNegativeInt(e.target.value))}
                              inputMode="numeric"
                            />
                            <span className="text-muted small">ч</span>
                            <Input
                              type="number"
                              placeholder="минуты"
                              min="0"
                              max="60"
                              step="1"
                              value={record.minutes}
                              onChange={(e) => this.updateWorkRecord(index, 'minutes', this.sanitizeNonNegativeInt(e.target.value, 60))}
                              inputMode="numeric"
                            />
                            <span className="text-muted small">мин</span>
                          </div>
                        ) : (
                          <div className="d-flex gap-2 align-items-center">
                            <Input
                              type="number"
                              placeholder={unit === WorkCategoryUnit.Pieces ? 'Количество' : 'Сумма'}
                              min="0"
                              step="1"
                              value={record.quantity}
                              onChange={(e) => this.updateWorkRecord(index, 'quantity', this.sanitizeNonNegativeInt(e.target.value))}
                              inputMode="numeric"
                            />
                            <span className="text-muted small">{unitMeta.inputLabel}</span>
                          </div>
                        )}
                      </FormGroup>

                      <FormGroup className="mb-0">
                        <Input
                          type="textarea"
                          rows="2"
                          placeholder="Описание работы"
                          value={record.comment}
                          onChange={(e) => this.updateWorkRecord(index, 'comment', e.target.value)}
                        />
                      </FormGroup>
                    </CardBody>
                  </Card>
                );
              })
            )}
          </CardBody>
        </Card>

        {/* Заметки */}
        <Card className="mb-3">
          <CardHeader>
            <h5 className="mb-0">📝 Дополнительные заметки</h5>
          </CardHeader>
          <CardBody>
            <FormGroup>
              <Input
                type="textarea"
                rows="3"
                placeholder="Дополнительная информация о выполненных работах (необязательно)"
                value={notes}
                onChange={(e) => this.setState({ notes: e.target.value })}
              />
            </FormGroup>
          </CardBody>
        </Card>

        {/* Итоги и отправка */}
        {workRecords.length > 0 && (
          <Card className="mb-3">
            <CardBody>
              <Button
                color="primary"
                size="lg"
                block
                onClick={this.submitWorkAct}
                disabled={submitting}
              >
                {submitting ? 'Отправка...' : '📤 Отправить отчет'}
              </Button>
            </CardBody>
          </Card>
        )}

        {/* Информация */}
        <Card className="mb-3">
          <CardBody className="text-muted small">
            <p className="mb-1">ℹ️ Ваш отчет будет отправлен менеджеру на проверку.</p>
          </CardBody>
        </Card>
      </Container>
    );
  }
}

export default EmployeeWorkForm;
